import { useState } from "react";
import { CheckCircle2, KeyRound, Loader2, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import PaymentSettings from "./PaymentSettings";

const labelCls =
  "mb-2 block text-[11px] font-semibold uppercase tracking-[0.2em] text-[#5C5C5C]";
const inputCls =
  "w-full rounded-xl border border-line bg-cream/50 px-4 py-3.5 text-sm text-ink outline-none transition-colors focus:border-rose";

export default function AdminSettings() {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const set = (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    setError("");
    setSuccess(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) {
      setError("New password must be at least 8 characters.");
      return;
    }
    if (form.next !== form.confirm) {
      setError("New passwords do not match.");
      return;
    }
    if (form.next === form.current) {
      setError("New password must be different from the current one.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/password", {
        current_password: form.current,
        new_password: form.next,
      });
      setSuccess(true);
      setForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        (Array.isArray(detail) ? detail[0]?.msg : detail) ||
          "Could not change the password. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <section data-testid="admin-settings" className="max-w-lg">
      <div className="rounded-3xl border border-line bg-white p-7 md:p-9">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rose/10">
            <KeyRound size={18} className="text-rose" />
          </span>
          <div>
            <h2 className="font-serif text-2xl font-medium">Change Password</h2>
            <p className="text-xs text-[#5C5C5C]">
              Your current session stays logged in; other devices are signed out.
            </p>
          </div>
        </div>

        <form data-testid="settings-form" onSubmit={submit} className="flex flex-col gap-5">
          <div>
            <label htmlFor="settings-current" className={labelCls}>
              Current Password
            </label>
            <input
              id="settings-current"
              data-testid="settings-current-password"
              type="password"
              value={form.current}
              onChange={(e) => set("current", e.target.value)}
              autoComplete="current-password"
              required
              className={inputCls}
            />
          </div>
          <div>
            <label htmlFor="settings-new" className={labelCls}>
              New Password
            </label>
            <input
              id="settings-new"
              data-testid="settings-new-password"
              type="password"
              value={form.next}
              onChange={(e) => set("next", e.target.value)}
              autoComplete="new-password"
              required
              minLength={8}
              className={inputCls}
            />
            <p className="mt-1.5 text-[11px] text-[#5C5C5C]">
              Minimum 8 characters.
            </p>
          </div>
          <div>
            <label htmlFor="settings-confirm" className={labelCls}>
              Confirm New Password
            </label>
            <input
              id="settings-confirm"
              data-testid="settings-confirm-password"
              type="password"
              value={form.confirm}
              onChange={(e) => set("confirm", e.target.value)}
              autoComplete="new-password"
              required
              className={inputCls}
            />
          </div>

          {error && (
            <p
              data-testid="settings-error"
              className="rounded-xl bg-red-50 px-4 py-3 text-xs font-medium text-red-600"
            >
              {error}
            </p>
          )}
          {success && (
            <p
              data-testid="settings-success"
              className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-xs font-medium text-emerald-700"
            >
              <CheckCircle2 size={15} /> Password changed successfully. Use it the
              next time you log in.
            </p>
          )}

          <button
            data-testid="settings-save-button"
            type="submit"
            disabled={saving}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-sm font-medium text-cream transition-colors duration-300 hover:bg-rose hover:text-white disabled:opacity-60"
          >
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Saving…
              </>
            ) : (
              <>
                <ShieldCheck size={16} /> Save New Password
              </>
            )}
          </button>
        </form>
      </div>

      <PaymentSettings />
    </section>
  );
}
